import { useState } from 'react';
import type { FormEvent } from 'react';

import type { NovaAtividade, NovaPergunta, NovaPista, NovoEnigma } from '../api/conteudo';

export type TipoDeItem = 'enigma' | 'pista' | 'atividade' | 'pergunta';
export type DadosDoItem = NovoEnigma | NovaPista | NovaAtividade | NovaPergunta;

const LETRAS = ['A', 'B', 'C', 'D'] as const;
/** O mesmo teto do servidor: a senha vira a quantidade de caixas no tablet. */
const TETO_DA_SENHA = 12;

interface Props {
    tipo: TipoDeItem;
    titulo: string;
    /** Devolve a mensagem de erro, ou `null` quando gravou. */
    aoEnviar: (dados: DadosDoItem) => Promise<string | null>;
    aoCancelar: () => void;
}

/** Formulário de inclusão: enigma (com a 1ª pista), pista, atividade (com a 1ª pergunta) ou pergunta. */
export function NovoItemForm({ tipo, titulo, aoEnviar, aoCancelar }: Props) {
    const temPista = tipo === 'enigma' || tipo === 'pista';
    const [charada, setCharada] = useState('');
    const [tituloAtividade, setTituloAtividade] = useState('');
    const [tempo, setTempo] = useState('');
    const [enunciado, setEnunciado] = useState('');
    const [senha, setSenha] = useState('');
    const [pontos, setPontos] = useState('1');
    const [opcoes, setOpcoes] = useState(['', '', '', '']);
    const [correta, setCorreta] = useState<(typeof LETRAS)[number] | ''>('');
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    function montar(): DadosDoItem | string {
        if (!enunciado.trim()) return temPista ? 'Escreva o enunciado da pista.' : 'Escreva o enunciado da pergunta.';
        if (temPista) {
            const limpa = senha.trim();
            if (!limpa || limpa.length > TETO_DA_SENHA) return `A senha tem de 1 a ${TETO_DA_SENHA} caracteres.`;
            const pista: NovaPista = { enunciado: enunciado.trim(), valor_esperado: limpa };
            if (tipo === 'pista') return pista;
            if (!charada.trim()) return 'Escreva a charada do enigma.';
            return { ...pista, texto_charada: charada.trim() };
        }
        const numero = Number(pontos);
        if (pontos === '' || !Number.isInteger(numero) || numero < 0) return 'Os pontos são um número inteiro, zero ou mais.';
        if (opcoes.some((texto) => !texto.trim())) return 'Preencha as quatro alternativas.';
        if (correta === '') return 'Marque a alternativa correta.';
        const pergunta: NovaPergunta = { enunciado: enunciado.trim(), pontos: numero, opcoes: opcoes.map((t) => t.trim()), correta };
        if (tipo === 'pergunta') return pergunta;
        if (!tituloAtividade.trim()) return 'Escreva o título da atividade.';
        const segundos = Number(tempo);
        if (tempo !== '' && (!Number.isInteger(segundos) || segundos <= 0)) return 'O tempo é um número inteiro de segundos, ou vazio.';
        return { titulo: tituloAtividade.trim(), tempo_segundos: tempo === '' ? null : segundos, pergunta };
    }

    async function enviar(evento: FormEvent) {
        evento.preventDefault();
        const dados = montar();
        if (typeof dados === 'string') {
            setErro(dados);
            return;
        }
        setEnviando(true);
        setErro(null);
        const falha = await aoEnviar(dados);
        // Com sucesso o painel fecha o formulário; só há o que atualizar se falhou.
        if (falha !== null) {
            setErro(falha);
            setEnviando(false);
        }
    }

    return (
        <form className="novo-item" aria-label={titulo} onSubmit={(e) => void enviar(e)}>
            <h4>{titulo}</h4>
            {tipo === 'enigma' && (
                <label>
                    Charada
                    <textarea value={charada} disabled={enviando} onChange={(e) => setCharada(e.target.value)} />
                </label>
            )}
            {tipo === 'atividade' && (
                <>
                    <label>
                        Título da atividade
                        <input type="text" value={tituloAtividade} disabled={enviando} onChange={(e) => setTituloAtividade(e.target.value)} />
                    </label>
                    <label>
                        Tempo (segundos; vazio = sem cronômetro)
                        <input type="number" step={1} min={1} value={tempo} disabled={enviando} onChange={(e) => setTempo(e.target.value)} />
                    </label>
                </>
            )}
            <label>
                {tipo === 'enigma' ? 'Enunciado da primeira pista' : tipo === 'pista' ? 'Enunciado da pista' : tipo === 'atividade' ? 'Enunciado da primeira pergunta' : 'Enunciado da pergunta'}
                <textarea value={enunciado} disabled={enviando} onChange={(e) => setEnunciado(e.target.value)} />
            </label>
            {temPista ? (
                <label>
                    Senha (o tamanho do campo no tablet é o tamanho da senha)
                    <input type="text" value={senha} maxLength={TETO_DA_SENHA} disabled={enviando} onChange={(e) => setSenha(e.target.value)} />
                </label>
            ) : (
                <>
                    <label>
                        Pontos
                        <input type="number" step={1} min={0} value={pontos} disabled={enviando} onChange={(e) => setPontos(e.target.value)} />
                    </label>
                    <fieldset className="alternativas">
                        <legend>Alternativas (marque a correta)</legend>
                        {LETRAS.map((letra, i) => (
                            <div key={letra} className="alternativa">
                                <input
                                    type="radio"
                                    name="correta"
                                    aria-label={`Alternativa ${letra} é a correta`}
                                    checked={correta === letra}
                                    disabled={enviando}
                                    onChange={() => setCorreta(letra)}
                                />
                                <input
                                    type="text"
                                    aria-label={`Texto da alternativa ${letra}`}
                                    placeholder={`Alternativa ${letra}`}
                                    value={opcoes[i]}
                                    disabled={enviando}
                                    onChange={(e) => setOpcoes(opcoes.map((texto, j) => (j === i ? e.target.value : texto)))}
                                />
                            </div>
                        ))}
                    </fieldset>
                </>
            )}
            {erro && <p className="mensagem-login" role="alert">{erro}</p>}
            <div className="acoes-novo-item">
                <button type="button" className="botao-sair" disabled={enviando} onClick={aoCancelar}>Cancelar</button>
                <button type="submit" className="botao-entrar" disabled={enviando}>{enviando ? 'Incluindo…' : 'Incluir'}</button>
            </div>
        </form>
    );
}
